import React, { useState } from 'react';
import { useSubscription } from '@/modules/subscriptions/hooks/useSubscription';
import { useSubscriptions } from '@/modules/subscriptions/api';
import * as Sentry from '@sentry/browser';

/**
 * Slim reminder shown on every screen (except mid-match) while a cardless
 * trial is active. The one-tap convert: billing portal, where adding a card
 * lets Stripe charge automatically when the trial ends.
 */
function TrialBanner() {
  const { isTrialing, trialDaysRemaining, loading: subscriptionLoading } = useSubscription();
  const { createPortalSession } = useSubscriptions();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [portalError, setPortalError] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  if (subscriptionLoading || !isTrialing || dismissed) return null;

  const daysCopy =
    trialDaysRemaining > 1
      ? `${trialDaysRemaining} days left`
      : trialDaysRemaining === 1
        ? 'last day'
        : 'ends today';

  const handleAddPayment = async () => {
    setPortalError(null);
    setIsRedirecting(true);
    try {
      const { url } = await createPortalSession();
      if (url) {
        window.location.href = url;
      } else {
        throw new Error('No portal URL returned');
      }
    } catch (error) {
      console.error('Error opening billing portal:', error);
      Sentry.captureException(error, { extra: { context: 'TrialBanner portal' } });
      setPortalError('Could not open billing. Check your connection and try again.');
      setIsRedirecting(false);
    }
  };

  return (
    <div
      role="status"
      className="bg-gradient-to-r from-brand-600 to-brand-700 text-white px-4 py-2.5"
    >
      <div className="max-w-4xl mx-auto flex items-center gap-3 text-sm">
        <span aria-hidden>⭐</span>
        <p className="flex-1 min-w-0">
          <span className="font-semibold">Premium trial — {daysCopy}.</span>{' '}
          <span className="hidden sm:inline">Add a card to keep saving matches after it ends.</span>
        </p>
        {portalError ? (
          <span className="shrink-0 text-xs underline">{portalError}</span>
        ) : (
          <button
            type="button"
            onClick={handleAddPayment}
            disabled={isRedirecting}
            className="shrink-0 font-semibold underline underline-offset-2 hover:no-underline disabled:opacity-70 cursor-pointer"
          >
            {isRedirecting ? 'Opening…' : 'Add payment details'}
          </button>
        )}
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss trial reminder"
          className="shrink-0 opacity-80 hover:opacity-100 cursor-pointer px-1"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

export default TrialBanner;
